import React, { FC, useState, useRef, useEffect } from 'react';
import { useNavigate, useMatch, useSearchParams, useLocation } from 'react-router-dom';

import { isValidSearchString } from '@sv-support-sdk/utils/src/helpers/search';

import { searchActions } from '../../../store/slices/search';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { searchResultsByValue } from '../../../store/actions/search';
import {
  selectDropdownList,
  selectLoading,
  selectSearchValue,
} from '../../../store/selectors/search';
import { routes } from '../../../routing';
import {
  DROPDOWN_RESULTS_LENGTH,
  DEFAULT_PER_PAGE,
  SEARCH_FIELD_ID,
} from '../../../constants/search';
import useMobileDetect from '../../../hooks/useMobileDetect';

import DesktopSearch from './DesktopSearch';
import MobileSearch from './MobileSearch';

interface IProps {
  isSearchOpened?: boolean;
  goBackSearchRoute?: string;
  toggleSearch?: (value: boolean) => void;
}

const Search: FC<IProps> = ({ isSearchOpened, goBackSearchRoute, toggleSearch }) => {
  const [isSearchInFocus, setIsSearchInFocus] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchValue = useAppSelector(selectSearchValue);
  const isSearchRoute = useMatch(routes.search);
  const [searchParams, setSearchParams] = useSearchParams();
  const dropdownList = useAppSelector(selectDropdownList);
  const loading = useAppSelector(selectLoading);
  const location = useLocation();
  const isMobile = useMobileDetect();
  const scopes = [
    { route: routes.$root, key: 'topic', isActive: useMatch(routes.$root) },
    { route: routes.articles, key: 'topic', isActive: useMatch(routes.articles) },
    { route: routes.faq, key: 'faq_topic', isActive: useMatch(routes.faq) },
    { route: routes.releaseNotes, key: 'release_note', isActive: useMatch(routes.releaseNotes) },
    { route: routes.tickets, key: 'ticket', isActive: useMatch(routes.tickets) },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById(SEARCH_FIELD_ID);

      if (dropdown && !dropdown.contains(event.target as Node)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const findActiveScope = () => {
    for (const scope of scopes) {
      if (scope.isActive) {
        return scope.key;
      }
    }

    for (const scope of scopes) {
      const { route, key } = scope;

      if (route !== routes.$root && location.pathname.includes(route)) {
        return key;
      }
    }

    return null;
  };

  const activeScope = findActiveScope();

  const handleInputChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(searchActions.setValue(value));

    if (value.trim().length >= 3) {
      if (isSearchRoute && isMobile) {
        dispatch(searchActions.setPagination({ perPage: DEFAULT_PER_PAGE }));
        dispatch(
          searchResultsByValue({
            skipCountRequest: false,
            searchValue: value,
          }),
        );

        setSearchParams({
          q: value,
          scope: searchParams.getAll('scope'),
        });
      } else {
        setIsDropdownVisible(true);
        dispatch(searchActions.setPagination({ perPage: DROPDOWN_RESULTS_LENGTH }));
        dispatch(
          searchResultsByValue({
            skipCountRequest: true,
            searchValue: value,
            scopes: activeScope ? [activeScope] : undefined,
            includeOtherScopes: activeScope ? 1 : undefined,
            dropdownMode: true,
          }),
        );
      }
    }
  };

  const handleInputKeyUp = ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
    if (key === 'Enter' && isValidSearchString(searchValue)) {
      dispatch(searchActions.setPagination({ perPage: DEFAULT_PER_PAGE }));
      setIsDropdownVisible(false);

      if (isMobile) {
        inputRef.current?.blur();
      }

      if (isSearchRoute) {
        dispatch(
          searchResultsByValue({
            skipCountRequest: false,
            searchValue,
          }),
        );

        setSearchParams({
          q: searchValue,
          scope: searchParams.getAll('scope'),
        });

        return;
      }

      navigate(`${routes.search}?q=${searchValue}`);
    }
  };

  const clearSearchValue = () => {
    setIsSearchInFocus(true);
    dispatch(searchActions.setValue(''));

    if (inputRef?.current) {
      inputRef.current.focus();
    }
  };

  const goBack = () => {
    if (!goBackSearchRoute || !toggleSearch) return;

    if (isSearchRoute) {
      navigate(goBackSearchRoute);
    } else {
      toggleSearch(false);
    }
  };

  const showDropdown = isMobile
    ? searchValue.trim().length >= 3 && !!isSearchOpened
    : searchValue.trim().length >= 3 && isSearchInFocus && isDropdownVisible;

  const navigateToSearchResultItem = (result: string) => {
    navigate(result);
    setIsSearchInFocus(false);
    setIsDropdownVisible(false);
  };

  const handleSearchAllResults = () => {
    dispatch(searchActions.setPagination({ perPage: DEFAULT_PER_PAGE }));

    if (isMobile) {
      window.scrollTo(0, 0);
    }

    navigate(`${routes.search}?q=${searchValue}`);
    setIsSearchInFocus(false);
    setIsDropdownVisible(false);
  };

  const onFocus = () => {
    setIsSearchInFocus(true);

    if (searchValue.trim().length >= 3) {
      setIsDropdownVisible(true);
    }

    if (isSearchRoute && dropdownList.length === 0) {
      setIsDropdownVisible(true);
      dispatch(searchActions.setPagination({ perPage: DROPDOWN_RESULTS_LENGTH }));
      dispatch(
        searchResultsByValue({
          skipCountRequest: true,
          dropdownMode: true,
          searchValue,
        }),
      );
    }
  };

  return isMobile ? (
    <MobileSearch
      ref={inputRef}
      showDropdown={showDropdown}
      searchValue={searchValue}
      loading={loading}
      isSearchOpened={isSearchOpened}
      handleInputChange={handleInputChange}
      handleInputKeyUp={handleInputKeyUp}
      clearSearchValue={clearSearchValue}
      handleSearchAllResults={handleSearchAllResults}
      goBack={goBack}
    />
  ) : (
    <DesktopSearch
      ref={inputRef}
      showDropdown={showDropdown}
      searchValue={searchValue}
      loading={loading}
      isSearchInFocus={isSearchInFocus}
      handleInputChange={handleInputChange}
      handleInputKeyUp={handleInputKeyUp}
      clearSearchValue={clearSearchValue}
      handleSearchAllResults={handleSearchAllResults}
      navigateToSearchResultItem={navigateToSearchResultItem}
      onFocus={onFocus}
    />
  );
};

export default Search;
